import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import { idFromObject } from '@sorare/core/src/gql/idFromObject';

import { Player } from 'components/MatchView/Player';

import type {
  FootballBench_anyPlayer,
  FootballBench_anyPlayerInterfaceSubscription,
} from './__generated__/index.graphql';

const Root = styled.div`
  --player-width: 80px;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, var(--player-width));
  grid-gap: var(--double-unit) 0;
  justify-content: space-evenly;
  align-items: flex-start;
`;

const Button = styled.button`
  min-width: var(--player-width);
`;

type Props = {
  benchPlayers: FootballBench_anyPlayer[];
  highlightedPlayerSlugs?: string[];
  onClick: (playerSlug: string, scoreId?: string) => void;
  desktop?: boolean;
};
export const FootballBench = ({
  benchPlayers,
  highlightedPlayerSlugs,
  onClick,
  desktop,
}: Props) => {
  const sortedPlayers = [...benchPlayers].sort((a, b) =>
    (a.playerGameScore?.score || 0) > (b.playerGameScore?.score || 0) ? -1 : 1
  );

  return (
    <Root className={classNames({ desktop })}>
      {sortedPlayers.map(player => (
        <Button
          key={player.slug}
          type="button"
          onClick={() =>
            onClick(player.slug, idFromObject(player.playerGameScore?.id))
          }
        >
          <Player
            player={player}
            highlighted={highlightedPlayerSlugs?.includes(player.slug)}
            highlightInLineupPlayers
            startedBench
          />
        </Button>
      ))}
    </Root>
  );
};

FootballBench.fragments = {
  anyPlayer: gql`
    fragment FootballBench_anyPlayer on AnyPlayerInterface {
      slug
      playerGameScore(gameId: $id) {
        id
        score
      }
      ...Player_anyPlayer
    }
    ${Player.fragments.anyPlayer}
  ` as TypedDocumentNode<FootballBench_anyPlayer>,
  subscriptions: {
    anyPlayerInterface: gql`
      fragment FootballBench_anyPlayerInterfaceSubscription on AnyPlayerInterface {
        slug
        ...Player_anyPlayerInterfaceSubscription
      }
      ${Player.fragments.subscriptions.anyPlayerInterface}
    ` as TypedDocumentNode<FootballBench_anyPlayerInterfaceSubscription>,
  },
};
